import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { getData, postData, putData, deleteData } from '../../../../backend/api';
import { GetIcon, AddIcon, DeleteIcon } from '../../../../icons';
import { AddButton } from '../../../elements/list/styles';
import { FormMainContainer, Overlay, Page, Footer } from '../../../elements/list/create/styles';
import { CloseButton } from '../../../elements/list/popup/styles';
import { Header as TopHeader } from '../../../elements/list/manage/styles';
import FormInput from '../../../elements/input';
import CustomSelect from '../../../elements/select';

// Styled Components
const Container = styled.div`
  padding: 20px;
`;

const StepSection = styled.div`
  margin-bottom: 24px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
`;

const StepHeader = styled.div`
  background: #f5f7fa;
  padding: 12px 16px;
  font-weight: 600;
  font-size: 16px;
  color: #333;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FieldsList = styled.div`
  padding: 8px;
`;

const FieldItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  margin: 4px 0;
  background: ${props => props.isDragging ? '#e3f2fd' : (props.isCore ? '#f8f9fa' : '#fff')};
  border: 1px solid ${props => props.isDragOver ? '#3fc4fd' : (props.isCore ? '#dee2e6' : '#e0e0e0')};
  border-radius: 6px;
  gap: 12px;
  transition: all 0.2s ease;
  opacity: ${props => props.isDragging ? 0.5 : 1};
  cursor: ${props => props.draggable ? 'grab' : 'default'};
  
  &:hover {
    background: ${props => props.isCore ? '#f8f9fa' : '#f5f7fa'};
  }
`;

const DragHandle = styled.span`
  cursor: ${props => props.disabled ? 'not-allowed' : 'grab'};
  color: ${props => props.disabled ? '#ccc' : '#888'};
  font-size: 18px;
`;

const FieldInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldName = styled.span`
  font-weight: 500;
  font-size: 16px;
  color: #333;
`;

const FieldMeta = styled.span`
  font-size: 14px;
  color: #888;
`;

const FieldBadge = styled.span`
  font-size: 13px;
  padding: 2px 8px;
  border-radius: 12px;
  background: ${props => {
    if (props.type === 'core') return '#e3f2fd';
    if (props.type === 'encrypted') return '#fff3e0';
    if (props.type === 'required') return '#ffebee';
    return '#e8f5e9';
  }};
  color: ${props => {
    if (props.type === 'core') return '#1565c0';
    if (props.type === 'encrypted') return '#ef6c00';
    if (props.type === 'required') return '#c62828';
    return '#2e7d32';
  }};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  color: ${props => props.danger ? '#dc3545' : '#666'};
  
  &:hover {
    background: ${props => props.danger ? '#ffebee' : '#f0f0f0'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AddFieldButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #f5f7fa;
  border: 1px dashed #ccc;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  color: #666;
  width: 100%;
  justify-content: center;
  margin-top: 8px;
  
  &:hover {
    background: #e8eef5;
    border-color: #999;
  }
`;

const ModalContent = styled.div`
  padding: 20px;
  max-height: 70vh;
  overflow-y: auto;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  margin-bottom: 6px;
  font-size: 16px;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 16px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #3fc4fd;
  }
`;

const Checkbox = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  cursor: pointer;
  margin-bottom: 8px;
  
  input {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
`;

const TabRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  border-bottom: 1px solid #eee;
  padding-bottom: 12px;
`;

const LangTab = styled.button`
  padding: 6px 16px;
  border: 1px solid ${props => props.active ? '#3fc4fd' : '#ddd'};
  background: ${props => props.active ? '#e3f7ff' : '#fff'};
  border-radius: 20px;
  cursor: pointer;
  font-size: 14px;
  font-weight: ${props => props.active ? '600' : '400'};
`;

const OptionsContainer = styled.div`
  margin-top: 16px;
  padding: 12px;
  background: #f9f9f9;
  border-radius: 8px;
`;

const OptionRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  align-items: center;
`;

const OptionInput = styled.input`
  flex: 1;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 15px;
`;

const RemoveOptionBtn = styled.button`
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  padding: 4px;
`;

// Core fields that cannot be removed
const CORE_FIELDS = {
  1: [{ name: 'department', label: 'Department', type: 'select', required: true }],
  2: [{ name: 'category', label: 'Category', type: 'select', required: false }],
  3: [
    { name: 'title', label: 'Title', type: 'text', required: true },
    { name: 'anonymity', label: 'Anonymity', type: 'radio', required: true, conditional: true },
    { name: 'description', label: 'Description', type: 'textarea', required: true },
    { name: 'file', label: 'File Upload', type: 'file', required: false, conditional: true }
  ]
};

const FIELD_TYPES = [
  { id: 'text', value: 'Text Input' },
  { id: 'textarea', value: 'Textarea' },
  { id: 'select', value: 'Dropdown Select' },
  { id: 'radio', value: 'Radio Buttons' },
  { id: 'date', value: 'Date Picker' },
  { id: 'number', value: 'Number Input' },
  { id: 'file', value: 'File Upload' },
  { id: 'email', value: 'Email Input' }
];

const STEP_OPTIONS = [
  { id: 1, value: 'Step 1 - Department Selection' },
  { id: 2, value: 'Step 2 - Category Selection' },
  { id: 3, value: 'Step 3 - Incident Details' }
];

const VISIBILITY_OPTIONS = [
  { id: 'both', value: 'Always visible' },
  { id: 'confidential', value: 'Only when Confidential' },
  { id: 'anonymous', value: 'Only when Anonymous' }
];

const FormFieldsManager = ({ channel, dispatch, navigate, setLoaderBox, setMessage }) => {
  const { t } = useTranslation();
  const [customFields, setCustomFields] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [activeLang, setActiveLang] = useState('de');
  
  const [formData, setFormData] = useState({
    fieldKey: '',
    fieldType: 'text',
    labels: { en: '', de: '' },
    placeholders: { en: '', de: '' },
    stepNumber: 1,
    required: false,
    isEncrypted: false,
    minLength: '',
    maxLength: '',
    options: [],
    visibleWhen: { enabled: false, anonymityValue: 'both' }
  });

  // Drag and Drop state
  const [draggedField, setDraggedField] = useState(null);
  const [dragOverField, setDragOverField] = useState(null);

  const handleDragStart = (e, field) => {
    setDraggedField(field);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', field._id);
  };

  const handleDragEnd = () => {
    setDraggedField(null);
    setDragOverField(null);
  };

  const handleDragOver = (e, field) => {
    e.preventDefault();
    if (draggedField && field._id !== draggedField._id && field.stepNumber === draggedField.stepNumber) {
      setDragOverField(field._id);
    }
  };

  const handleDragLeave = () => {
    setDragOverField(null);
  };

  const handleDrop = async (e, targetField) => {
    e.preventDefault();
    if (!draggedField || draggedField._id === targetField._id) {
      handleDragEnd();
      return;
    }

    // Only allow reordering within the same step
    if (draggedField.stepNumber !== targetField.stepNumber) {
      handleDragEnd();
      return;
    }

    // Get fields for the step and reorder
    const stepFields = customFields
      .filter(f => f.stepNumber === draggedField.stepNumber)
      .sort((a, b) => a.order - b.order);

    const draggedIndex = stepFields.findIndex(f => f._id === draggedField._id);
    const targetIndex = stepFields.findIndex(f => f._id === targetField._id);

    // Reorder array
    const reordered = [...stepFields];
    reordered.splice(draggedIndex, 1);
    reordered.splice(targetIndex, 0, draggedField);

    // Update order values
    const updates = reordered.map((field, index) => ({
      id: field._id,
      order: index
    }));

    // Optimistic update
    const updatedCustomFields = customFields.map(f => {
      const update = updates.find(u => u.id === f._id);
      if (update) {
        return { ...f, order: update.order };
      }
      return f;
    });
    setCustomFields(updatedCustomFields);

    handleDragEnd();

    // Save to backend
    try {
      await putData({ updates }, 'custom-form-field/bulk-update', dispatch, navigate);
    } catch (error) {
      console.error('Error updating field order:', error);
      fetchFields(); // Revert on error
    }
  };

  // Fetch custom fields for this channel
  const fetchFields = async () => {
    if (!channel?._id) return;
    
    try {
      setIsLoading(true);
      const response = await getData({ channel: channel._id }, 'custom-form-field', dispatch, navigate);
      if (response.data?.data) {
        setCustomFields(response.data.data);
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching custom fields:', error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFields();
  }, [channel?._id]);

  const resetForm = () => {
    setFormData({
      fieldKey: '',
      fieldType: 'text',
      labels: { en: '', de: '' },
      placeholders: { en: '', de: '' },
      stepNumber: 1,
      required: false,
      isEncrypted: false,
      minLength: '',
      maxLength: '',
      options: [],
      visibleWhen: { enabled: false, anonymityValue: 'both' }
    });
    setEditingField(null);
    setActiveLang('de');
  };

  const openAddModal = (stepNumber) => {
    resetForm();
    setFormData(prev => ({ ...prev, stepNumber }));
    setShowModal(true);
  };

  const openEditModal = (field) => {
    setEditingField(field);
    setFormData({
      fieldKey: field.fieldKey,
      fieldType: field.fieldType,
      labels: field.labels || { en: '', de: '' },
      placeholders: field.placeholders || { en: '', de: '' },
      stepNumber: field.stepNumber,
      required: field.required || false,
      isEncrypted: field.isEncrypted || false,
      minLength: field.minLength || '',
      maxLength: field.maxLength || '',
      options: field.options || [],
      visibleWhen: field.visibleWhen || { enabled: false, anonymityValue: 'both' }
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLabelChange = (lang, value) => {
    setFormData(prev => ({
      ...prev,
      labels: { ...prev.labels, [lang]: value }
    }));
    
    // Auto-generate fieldKey from English label if creating new field
    if (!editingField && lang === 'en') {
      const key = value.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
      setFormData(prev => ({ ...prev, fieldKey: key }));
    }
  };

  const handlePlaceholderChange = (lang, value) => {
    setFormData(prev => ({
      ...prev,
      placeholders: { ...prev.placeholders, [lang]: value }
    }));
  };

  const addOption = () => {
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, { value: '', labels: { en: '', de: '' } }]
    }));
  };

  const updateOption = (index, lang, value) => {
    setFormData(prev => {
      const newOptions = [...prev.options];
      if (lang === 'value') {
        newOptions[index].value = value;
      } else {
        newOptions[index].labels = { ...newOptions[index].labels, [lang]: value };
      }
      return { ...prev, options: newOptions };
    });
  };

  const removeOption = (index) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.labels.de) {
      setMessage({ type: 1, content: t('German label is required'), proceed: 'Okay', status: 400});
      return;
    }
    
    if (!formData.fieldKey) {
      setMessage({ type: 1, content: t('Field key is required') , proceed: 'Okay', status: 400 });
      return;
    }

    try {
      setLoaderBox(true);
      
      const payload = {
        ...formData,
        channel: channel._id,
        minLength: formData.minLength ? parseInt(formData.minLength) : undefined,
        maxLength: formData.maxLength ? parseInt(formData.maxLength) : undefined
      };

      if (editingField) {
        await putData(payload, `custom-form-field/${editingField._id}`, dispatch, navigate);
        setMessage({ type: 1, content: t('Field updated successfully'), proceed: t('okay'), code: 200 });
      } else {
        await postData(payload, 'custom-form-field', dispatch, navigate);
        setMessage({ type: 1, content: t('Field created successfully'), proceed: t('okay'), code: 200 });
      }
      
      setLoaderBox(false);
      closeModal();
      fetchFields();
    } catch (error) {
      setLoaderBox(false);
      setMessage({ type: 1, content: error.message || 'Error saving field', proceed: 'Okay' });
    }
  };

  const handleDelete = async (fieldId) => {
    if (!window.confirm(t('Are you sure you want to delete this field?'))) {
      return;
    }

    try {
      setLoaderBox(true);
      await deleteData({}, 'custom-form-field', fieldId, dispatch, navigate);
      setLoaderBox(false);
      setMessage({ type: 1, content: t('Field deleted successfully'), proceed: t('okay'), code: 200 });
      fetchFields();
    } catch (error) {
      setLoaderBox(false);
      setMessage({ type: 1, content: error.message || 'Error deleting field', proceed: 'Okay' });
    }
  };

  const getFieldsForStep = (stepNumber) => {
    return customFields.filter(f => f.stepNumber === stepNumber).sort((a, b) => a.order - b.order);
  };

  const renderStep = (stepNumber, stepTitle) => {
    const coreFields = CORE_FIELDS[stepNumber] || [];
    const stepCustomFields = getFieldsForStep(stepNumber);
    const lang = sessionStorage.getItem('_lang') || 'de';

    return (
      <StepSection key={stepNumber}>
        <StepHeader>
          <span>{t(stepTitle)}</span>
        </StepHeader>
        <FieldsList>
          {/* Core Fields */}
          {coreFields.map((field, index) => (
            <FieldItem key={`core-${field.name}`} isCore>
              <DragHandle disabled>☰</DragHandle>
              <FieldInfo>
                <FieldName>{t(field.label)}</FieldName>
                <FieldMeta>{t(field.type)}</FieldMeta>
              </FieldInfo>
              <FieldBadge type="core">{t('Core Field')}</FieldBadge>
              {field.required && <FieldBadge type="required">{t('Required')}</FieldBadge>}
              {field.conditional && <FieldBadge type="optional">{t('Conditional')}</FieldBadge>}
              <ActionButtons>
                <IconButton disabled title={t('Core fields cannot be edited')}>
                  <GetIcon icon="edit" />
                </IconButton>
              </ActionButtons>
            </FieldItem>
          ))}
          
          {/* Custom Fields */}
          {stepCustomFields.map((field) => (
            <FieldItem 
              key={field._id}
              draggable
              isDragging={draggedField?._id === field._id}
              isDragOver={dragOverField === field._id}
              onDragStart={(e) => handleDragStart(e, field)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, field)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, field)}
            >
              <DragHandle>☰</DragHandle>
              <FieldInfo>
                <FieldName>{field.labels?.[lang] || field.labels?.de || field.fieldKey}</FieldName>
                <FieldMeta>{t(field.fieldType)} • {field.fieldKey}</FieldMeta>
              </FieldInfo>
              {field.isEncrypted && <FieldBadge type="encrypted">{t('Encrypted')}</FieldBadge>}
              {field.required && <FieldBadge type="required">{t('Required')}</FieldBadge>}
              {field.visibleWhen?.enabled && (
                <FieldBadge type="optional">
                  {field.visibleWhen.anonymityValue === 'confidential' ? t('Confidential only') : 
                   field.visibleWhen.anonymityValue === 'anonymous' ? t('Anonymous only') : ''}
                </FieldBadge>
              )}
              <ActionButtons>
                <IconButton onClick={() => openEditModal(field)} title={t('Edit')}>
                  <GetIcon icon="edit" />
                </IconButton>
                <IconButton danger onClick={() => handleDelete(field._id)} title={t('Delete')}>
                  <DeleteIcon />
                </IconButton>
              </ActionButtons>
            </FieldItem>
          ))}
          
          <AddFieldButton onClick={() => openAddModal(stepNumber)}>
            <AddIcon /> {t('Add Custom Field')}
          </AddFieldButton>
        </FieldsList>
      </StepSection>
    );
  };

  const showOptionsInput = ['select', 'radio'].includes(formData.fieldType);

  return (
    <Container>
      {renderStep(1, 'Step 1 - Department Selection')}
      {renderStep(2, 'Step 2 - Category Selection')}
      {renderStep(3, 'Step 3 - Incident Details')}

      {/* Add/Edit Modal */}
      {showModal && (
        <Overlay className="popup">
          <Page className="double" style={{ maxWidth: '600px' }}>
            <TopHeader className="popup">
              <span>{editingField ? t('Edit Field') : t('Add Custom Field')}</span>
              <CloseButton onClick={closeModal}>
                <GetIcon icon="Close" />
              </CloseButton>
            </TopHeader>
            
            <ModalContent>
              {/* Language Tabs */}
              <TabRow>
                <LangTab active={activeLang === 'de'} onClick={() => setActiveLang('de')}>
                  {t("de")}
                </LangTab>
                <LangTab active={activeLang === 'en'} onClick={() => setActiveLang('en')}>
                  {t("en")}
                </LangTab>
              </TabRow>

              {/* Label */}
              <FormGroup>
                <Label>{t('Field Label')} ({activeLang.toUpperCase()}) *</Label>
                <Input
                  value={formData.labels[activeLang]}
                  onChange={(e) => handleLabelChange(activeLang, e.target.value)}
                  placeholder={t('Enter field label')}
                />
              </FormGroup>

              {/* Placeholder */}
              <FormGroup>
                <Label>{t('Placeholder')} ({activeLang.toUpperCase()})</Label>
                <Input
                  value={formData.placeholders[activeLang]}
                  onChange={(e) => handlePlaceholderChange(activeLang, e.target.value)}
                  placeholder={t('Enter placeholder text')}
                />
              </FormGroup>

              {/* Field Key (auto-generated or manual) */}
              <FormGroup>
                <Label>{t('Field Key')} *</Label>
                <Input
                  value={formData.fieldKey}
                  onChange={(e) => handleInputChange('fieldKey', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  placeholder="field_key"
                  disabled={!!editingField}
                />
              </FormGroup>

              {/* Field Type */}
              <FormGroup>
                <CustomSelect
                  id="fieldType"
                  name="fieldType"
                  label={t('Field Type')}
                  value={formData.fieldType}
                  formValues={formData}
                  placeholder={t('Select type')}
                  apiType="JSON"
                  selectApi={[
                    { id: 'text', value: t('Text Input') },
                    { id: 'textarea', value: t('Textarea') },
                    { id: 'select', value: t('Dropdown Select') },
                    { id: 'radio', value: t('Radio Buttons') },
                    { id: 'date', value: t('Date Picker') },
                    { id: 'number', value: t('Number Input') },
                    { id: 'file', value: t('File Upload') },
                    { id: 'email', value: t('Email Input') }
                  ]}
                  onSelect={(_, e) => handleInputChange('fieldType', e.id)}
                />
              </FormGroup>

              {/* Step Number */}
              <FormGroup>
                <CustomSelect
                  id="stepNumber"
                  name="stepNumber"
                  label={t('Step')}
                  value={formData.stepNumber}
                  formValues={formData}
                  placeholder={t('Select step')}
                  apiType="JSON"
                  selectApi={[
                    { id: 1, value: t('Step 1 - Department Selection') },
                    { id: 2, value: t('Step 2 - Category Selection') },
                    { id: 3, value: t('Step 3 - Incident Details') }
                  ]}
                  onSelect={(_, e) => handleInputChange('stepNumber', e.id)}
                />
              </FormGroup>

              {/* Options for Select/Radio */}
              {showOptionsInput && (
                <OptionsContainer>
                  <Label>{t('Options')}</Label>
                  {formData.options.map((option, index) => (
                    <OptionRow key={index}>
                      <OptionInput
                        value={option.value}
                        onChange={(e) => updateOption(index, 'value', e.target.value)}
                        placeholder={t('value')}
                        style={{ maxWidth: '100px' }}
                      />
                      <OptionInput
                        value={option.labels?.[activeLang] || ''}
                        onChange={(e) => updateOption(index, activeLang, e.target.value)}
                        placeholder={`${t('Label')} (${activeLang.toUpperCase()})`}
                      />
                      <RemoveOptionBtn onClick={() => removeOption(index)}>
                        <DeleteIcon />
                      </RemoveOptionBtn>
                    </OptionRow>
                  ))}
                  <AddFieldButton onClick={addOption} style={{ marginTop: '8px' }}>
                    <AddIcon /> {t('Add Option')}
                  </AddFieldButton>
                </OptionsContainer>
              )}

              {/* Validation */}
              <FormGroup style={{ marginTop: '16px' }}>
                <Checkbox>
                  <input
                    type="checkbox"
                    checked={formData.required}
                    onChange={(e) => handleInputChange('required', e.target.checked)}
                  />
                  {t('Required field')}
                </Checkbox>
                
                <Checkbox>
                  <input
                    type="checkbox"
                    checked={formData.isEncrypted}
                    onChange={(e) => handleInputChange('isEncrypted', e.target.checked)}
                  />
                  {t('Encrypt field value')}
                </Checkbox>
              </FormGroup>

              {/* Min/Max Length */}
              {['text', 'textarea', 'email'].includes(formData.fieldType) && (
                <FormGroup>
                  <Label>{t('Length Validation')}</Label>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <Input
                      type="number"
                      value={formData.minLength}
                      onChange={(e) => handleInputChange('minLength', e.target.value)}
                      placeholder={t('Min')}
                      style={{ width: '100px' }}
                    />
                    <Input
                      type="number"
                      value={formData.maxLength}
                      onChange={(e) => handleInputChange('maxLength', e.target.value)}
                      placeholder={t('Max')}
                      style={{ width: '100px' }}
                    />
                  </div>
                </FormGroup>
              )}

              {/* Conditional Visibility (only for step 3) */}
              {formData.stepNumber === 3 && (
                <FormGroup>
                  <Checkbox>
                    <input
                      type="checkbox"
                      checked={formData.visibleWhen.enabled}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        visibleWhen: { ...prev.visibleWhen, enabled: e.target.checked }
                      }))}
                    />
                    {t('Show only for specific anonymity type')}
                  </Checkbox>
                  
                  {formData.visibleWhen.enabled && (
                    <CustomSelect
                      id="anonymityValue"
                      name="anonymityValue"
                      label=""
                      value={formData.visibleWhen.anonymityValue}
                      formValues={formData.visibleWhen}
                      placeholder={t('Select visibility')}
                      apiType="JSON"
                      selectApi={[
                        { id: 'both', value: t('Always visible') },
                        { id: 'confidential', value: t('Only when Confidential') },
                        { id: 'anonymous', value: t('Only when Anonymous') }
                      ]}
                      onSelect={(_, e) => setFormData(prev => ({
                        ...prev,
                        visibleWhen: { ...prev.visibleWhen, anonymityValue: e.id }
                      }))}
                    />
                  )}
                </FormGroup>
              )}
            </ModalContent>

            <Footer>
              <FormInput type="close" value={t('cancel')} onChange={closeModal} />
              <FormInput type="submit" name="submit" value={editingField ? t('Update') : t('create')} onChange={handleSubmit} />
            </Footer>
          </Page>
        </Overlay>
      )}
    </Container>
  );
};

export default FormFieldsManager;
